import React, { ReactNode } from 'react';
import { format, isSameDay, parse } from 'date-fns';
import { skipToken } from '@reduxjs/toolkit/query';
import { endOfDay, startOfDay } from '@constants/dayRanger';
import { TSchedule, TWorkingDays } from '@pages/Physicians/components/PhysiciansTable/types';
import { DaySlot } from '@pages/Schedule/components/Day/DaySlot';
import { dayRangeHeight, rangeStep } from '@pages/Schedule/constants/ScheduleSettings';
import { useScheduleContext } from '@pages/Schedule/context/useScheduleContext';
import { generateUnavailableSlots } from '@pages/Schedule/utils/generateUnavailableSlots';
import { getAppointmentsForDate } from '@pages/Schedule/utils/getBlackoutsForDate';
import { getBlackourLocationForDate } from '@pages/Schedule/utils/getBlackoutsLocationForDate';
import { intervalsGroup } from '@constants/options/timeOptions';
import { Loader } from '@components/Loader';
import { IScheduleResponse } from '@api/schedule/types';
import { TAppointment } from '@api/appointments/types';
import { useFetchLocationBlackoutsQuery } from '@api/blackout/blackoutAPI';
import { getUnavailableTimeRanges } from '@helpers/getFreeOfDay';
import { useGetLocationByIdQuery } from '@api/locations/locationsAPI';

interface IDayBodyProps {
  appointments: IScheduleResponse | undefined;
  isLoading?: boolean;
}

export const DayBody = ({ appointments, isLoading = false }: IDayBodyProps) => {
  const {
    selectedDate,
    selectedPhysician,
    selectedHourFrom,
    selectedHourTo,
    selectedLocationId,
    physicians,
  } = useScheduleContext();

  const appointmentsList: TAppointment[] | undefined = appointments?.data;

  const currentDateAppointments = appointmentsList?.filter(({ date }: { date: string }) =>
    isSameDay(parse(date, 'MM/dd/yyyy', new Date()), selectedDate),
  );

  const startTime =
    intervalsGroup()[intervalsGroup().findIndex((time) => time.value === selectedHourFrom) - 1]
      ?.value || '';

  const endTime = selectedHourTo || '';

  const tableData = intervalsGroup(startTime || startOfDay || undefined, endTime || undefined);

  const intervalHeight = dayRangeHeight;

  const { data: locationsBlackout } = useFetchLocationBlackoutsQuery(
    !selectedLocationId ? skipToken : selectedLocationId,
  );

  const { data: location } = useGetLocationByIdQuery(selectedLocationId ?? skipToken, {
    skip: !selectedLocationId,
  });

  const blackoutLocationsSlots = getBlackourLocationForDate(selectedDate, locationsBlackout?.data);

  const hasNonEmptyStartTime = blackoutLocationsSlots?.some(
    (slot) => slot.startTime.trim() !== '' && slot.locationId === selectedLocationId,
  );

  const tableBody = tableData.reduce<ReactNode[]>((acc, currentTime, rowIndex) => {
    const tableRow = physicians?.data?.results
      .filter((physician) =>
        selectedPhysician?.length ? physician.id === selectedPhysician : physician,
      )
      .filter((physician) => physician?.schedule?.locationId === selectedLocationId)
      .map((physician) => {
        const currentPhysicianAppointments = currentDateAppointments?.filter(
          ({ trials }) => trials.physicians.id === physician.id,
        );

        const getStartTime = (time: string) => {
          const intervals = time.split(' - ');

          return intervals[0];
        };

        const currentAppointment = currentPhysicianAppointments?.filter(
          ({ time }: { time: string }) => getStartTime(time) === currentTime.value,
        );

        const currentWeekDay = format(selectedDate, 'eeee').toLowerCase();

        const { schedule: physicianSchedule, blackout: blackoutSchedule } = physician;

        const blackoutSlots = getAppointmentsForDate(
          selectedDate,
          blackoutSchedule,
          physician.id,
          startOfDay,
          endOfDay,
        );

        const currentBlackout = blackoutSlots?.find(
          ({ startTime: blackoutStartTime }: { startTime: string }) =>
            getStartTime(blackoutStartTime) === currentTime.value,
        );

        const locationSchedule: TWorkingDays =
          'days' in (location?.data?.schedule || {})
            ? (location?.data?.schedule as TSchedule).days
            : (location?.data?.schedule as TWorkingDays);

        const physicianScheduleDays: TWorkingDays =
          'days' in (physicianSchedule || {})
            ? (physicianSchedule as TSchedule).days
            : (physicianSchedule as TWorkingDays);

        if (
          !physicianSchedule ||
          !locationSchedule ||
          !physicianScheduleDays[currentWeekDay as keyof TWorkingDays]
        ) {
          return (
            <div key={`row-item-${physician.id}`} className="schedule-cell table-cell">
              <DaySlot start={startTime} duration={tableData.length * rangeStep} />
            </div>
          );
        }

        const availableTimeRanges = getUnavailableTimeRanges(
          locationSchedule,
          physicianScheduleDays,
        );

        const unavailableIntervals = generateUnavailableSlots(
          availableTimeRanges,
          currentWeekDay,
          tableData,
          startTime,
          endTime,
        );

        const currentUnavailableSlotStart = unavailableIntervals?.find(
          ({ from }) => from === currentTime.value,
        );

        return (
          <div key={`row-item-${physician.id}`} className="schedule-cell table-cell">
            <div className="schedule-cell-container">
              {currentBlackout && (
                <div className="schedule-cell-appointment">
                  <DaySlot
                    start={currentBlackout.startTime}
                    appointmentId={currentBlackout.id}
                    physicianId={physician.id}
                    locationId={selectedLocationId || ''}
                    personalBlackout={currentBlackout}
                    duration={Number(currentBlackout.duration)}
                    eventType="Blackout"
                  />
                </div>
              )}
              {currentAppointment?.map((appointment) => (
                <div className="schedule-cell-appointment" key={appointment.id}>
                  <DaySlot
                    start={appointment.time}
                    appointmentId={appointment.id}
                    duration={Number(appointment.duration)}
                    patient={`${appointment.patients.name} ${appointment.patients.surname}`}
                    eventType={appointment.type}
                    patientNumber={appointment.userIdFromSponsor}
                    isAdult={!!appointment.adult}
                  />
                </div>
              ))}
              {currentUnavailableSlotStart && !!currentUnavailableSlotStart.stepsInRange && (
                <div className="schedule-cell-appointment">
                  <DaySlot
                    start={startTime}
                    eventType="Unavailable"
                    duration={currentUnavailableSlotStart.stepsInRange * rangeStep}
                  />
                </div>
              )}
            </div>
          </div>
        );
      });

    const getStartTime = (time: string) => {
      const intervals = time.split(' - ');

      return intervals[0];
    };

    const currentBlackoutLocation = blackoutLocationsSlots?.find(
      ({ startTime: blackoutStartTime }: { startTime: string }) =>
        getStartTime(blackoutStartTime) === currentTime.value,
    );

    return [
      ...acc,
      <div
        className="schedule-row table-row"
        key={`table-row-slot${rowIndex}`}
        style={{
          ['height' as string]: `${intervalHeight}px`,
        }}
      >
        <div className="schedule-cell table-cell time-colum">{`${tableData[rowIndex].label}`}</div>
        {hasNonEmptyStartTime && (
          <div className="schedule-cell table-cell blackout-location">
            {currentBlackoutLocation && (
              <div className="schedule-cell-appointment">
                <DaySlot
                  start={currentBlackoutLocation.startTime}
                  duration={Number(currentBlackoutLocation.duration)}
                  personalBlackout={currentBlackoutLocation}
                  locationId={selectedLocationId || ''}
                  eventType="Blackout Location"
                />
              </div>
            )}
          </div>
        )}
        {tableRow}
      </div>,
    ];
  }, []);

  return isLoading ? <Loader /> : <div className="schedule-body table-body">{tableBody}</div>;
};
